// src/containers/VehicleList/VehicleDisplay/VehicleTab/MpiFormBuilder/components/DynamicSimpleChecklist.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { Box, Stack, Typography, Checkbox, Button, TextField, Select, MenuItem, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { updateMpiWorkItem, updateMpiItemRaw } from '../../../../../../actions/apiMpiWorkItems';
import { useDispatch } from 'react-redux';

const DynamicSimpleChecklist = ({
  items,
  headers = [],
  sectionId,
  sectionLabel = '',
  active_vehicle,
  vehicle_id,
  dispatch: parentDispatch,
  refreshVehicle,
  setError,
  current_user_profile_id,
  addCustomRow,
  headerColor = '#f5f5f5',
  fontColor = '#000000',
  headerFontColor = '#000000',
  onApproveAllSection,
  work_item_presets = [],
}) => {
  const dispatch = parentDispatch || useDispatch();
  const [localValues, setLocalValues] = useState({});

  // Name editing for custom rows
  const [editingItemId, setEditingItemId] = useState(null);
  const [tempName, setTempName] = useState('');

  const isTruthy = (value) => value === true || value === 'true' || value === 1 || value === '1';

  const normalizeValueForUpdate = (field, value, inputType) => {
    if (['checkbox', 'userstamp'].includes(inputType)) {
      return isTruthy(value) ? "true" : "false";
    }
    return value == null ? "" : String(value);
  };

  const parsedHeaders = useMemo(() => headers.map(h => {
    const parts = h.label.split(':').map(p => p.trim());
    const cleanLabel = parts[0];
    const icon = h.icon || (parts.length > 1 ? parts[1] : null);
    const true_color = h.true_color || (parts.length > 2 ? parts[2] : '#666');
    return { ...h, label: cleanLabel, icon, true_color };
  }), [headers]);

  const getInputType = useCallback((field, item) => {
    const preset = work_item_presets.find(p => p.id === item.work_item_preset_id) || {};
    if (['left_front', 'right_front', 'left_rear', 'right_rear'].includes(field)) return 'text';
    return preset[`${field}_inputtype`] || parsedHeaders.find(h => h.field === field)?.inputtype || 'checkbox';
  }, [work_item_presets, parsedHeaders]);

  const getDisplayValue = useCallback((itemId, field) => {
    const key = `${itemId}-${field}`;
    return localValues[key] !== undefined ? localValues[key] : (items.find(i => i.id === itemId)?.[field] ?? '');
  }, [localValues, items]);

  const handleTextInputChange = useCallback((itemId, field, value) => {
    setLocalValues(prev => ({ ...prev, [`${itemId}-${field}`]: value }));
  }, []);

  const handleTextCommit = useCallback(async (itemId, field, newValue = null) => {
    let value = newValue !== null ? newValue : localValues[`${itemId}-${field}`];
    if (value === undefined || value === null) return;

    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const inputType = getInputType(field, item);
    const normalizedValue = normalizeValueForUpdate(field, value, inputType);

    const updates = {
      [field]: normalizedValue,
      vehicle_id: active_vehicle.id || vehicle_id,
      form: item.form,
      section: item.section,
    };

    try {
      await dispatch(updateMpiWorkItem(itemId, updates));
      await refreshVehicle?.();

      setLocalValues(prev => {
        const newValues = { ...prev };
        delete newValues[`${itemId}-${field}`];
        return newValues;
      });
    } catch (err) {
      console.error('[handleTextCommit] FAILED:', err);
      setError?.('Failed to update item');
    }
  }, [localValues, items, getInputType, dispatch, refreshVehicle, active_vehicle, vehicle_id, setError]);

  // Name editing for custom rows
  const handleStartEdit = (item) => {
    if (item.work_item_preset_id === null) {
      setEditingItemId(item.id);
      setTempName(item.name || '');
    }
  };

  const handleSaveName = async () => {
    if (!tempName.trim() || !editingItemId) {
      setEditingItemId(null);
      setTempName('');
      return;
    }

    // Pass the new name explicitly
    await handleTextCommit(editingItemId, 'name', tempName.trim());
    setEditingItemId(null);
    setTempName('');
  };

  const handleToggle = useCallback(async (itemId, field, currentValue) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const inputType = getInputType(field, item);
    const newValue = normalizeValueForUpdate(field, !isTruthy(currentValue), inputType);

    const updates = {
      [field]: newValue,
      vehicle_id: active_vehicle.id || vehicle_id,
      form: item.form,
      section: item.section,
    };

    if (inputType === 'userstamp' || inputType === 'checkbox') {
      updates[`${field}_by_uid`] = current_user_profile_id;
      updates[`${field}_by_date`] = new Date().toISOString();
    }

    try {
      await dispatch(updateMpiWorkItem(itemId, updates));
      await refreshVehicle?.();
    } catch (err) {
      setError?.('Failed to update item');
    }
  }, [items, getInputType, dispatch, refreshVehicle, active_vehicle, vehicle_id, current_user_profile_id, setError]);

  const FourWheelInput = React.memo(({ item }) => {
    const fields = ['left_front', 'right_front', 'left_rear', 'right_rear'];
    const labels = ['L/F', 'R/F', 'L/R', 'R/R'];

    const [wheelValues, setWheelValues] = useState(() => ({
      left_front: item.left_front ?? '',
      right_front: item.right_front ?? '',
      left_rear: item.left_rear ?? '',
      right_rear: item.right_rear ?? '',
    }));

    const handleWheelChange = useCallback((field, value) => {
      setWheelValues(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleWheelBlur = useCallback(async (field) => {
      const value = wheelValues[field];
      const updates = {
        [field]: value,
        vehicle_id: active_vehicle.id || vehicle_id,
        form: item.form,
        section: item.section,
      };

      try {
        await dispatch(updateMpiItemRaw(item.id, updates));
      } catch (err) {
        console.error('[FourWheelInput] Raw save failed:', err);
        setError?.('Failed to update item');
      }
    }, [wheelValues, item, active_vehicle, vehicle_id, dispatch, setError]);

    return (
      <Box sx={{ width: '100%', py: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: fontColor }}>
          {item.name || item.description || '4 Wheel Input'}
        </Typography>

        <Stack spacing={1}>
          {/* First row: L/F and R/F */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {fields.slice(0, 2).map((field, i) => (
              <Box key={field} sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: fontColor, 
                    opacity: 0.85, 
                    width: 36, 
                    textAlign: 'right', 
                    mr: 1,
                    fontWeight: 500 
                  }}
                >
                  {labels[i]}
                </Typography>
                <TextField
                  size="small"
                  sx={{ flex: 1 }}
                  value={wheelValues[field]}
                  onChange={(e) => handleWheelChange(field, e.target.value)}
                  onBlur={() => handleWheelBlur(field)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                />
              </Box>
            ))}
          </Stack>

          {/* Second row: L/R and R/R */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {fields.slice(2).map((field, i) => (
              <Box key={field} sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: fontColor, 
                    opacity: 0.85, 
                    width: 36, 
                    textAlign: 'right', 
                    mr: 1,
                    fontWeight: 500 
                  }}
                >
                  {labels[i + 2]}
                </Typography>
                <TextField
                  size="small"
                  sx={{ flex: 1 }}
                  value={wheelValues[field]}
                  onChange={(e) => handleWheelChange(field, e.target.value)}
                  onBlur={() => handleWheelBlur(field)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                />
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
    );
  });

  if (!items?.length) {
    return <Box sx={{ p: 2 }}><Typography sx={{ fontStyle: 'italic', color: fontColor, opacity: 0.6 }}>No items in this section.</Typography></Box>;
  }

  const actionHeaders = parsedHeaders.filter(h => h.field !== 'name');

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: headerColor, px: 2, py: 1, borderRadius: '8px 8px 0 0' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: headerFontColor, pr: 2 }}>
          {sectionLabel}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, justifyContent: 'flex-end', gap: 1 }}>
          {actionHeaders.map((header) => {
            const { field } = header;
            const inputType = getInputType(field, items[0] || {});
            if (inputType === 'remove') return null;

            let iconWidth = 46;
            if (inputType === 'text' || inputType === 'textarea') iconWidth = 130;
            if (inputType === 'select') iconWidth = 145;
            if (inputType === 'select-wide') iconWidth = 210;
            if (inputType === 'radio') iconWidth = 170;

            return (
              <Box key={field} sx={{ width: iconWidth, textAlign: 'center', flexShrink: 0 }}>
                {header.icon && (
                  <i className={`far fa-${header.icon}`} style={{ fontSize: '21px', color: header.true_color || '#666' }} />
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Rows */}
      {items.map((item) => {
        const isCustom = item.work_item_preset_id === null;
        const isEditingName = editingItemId === item.id;
        const isFourWheel = item.layout_type === '4_wheel_input' || 
                            work_item_presets.some(p => p.id === item.work_item_preset_id && p.layout_type === '4_wheel_input');

        return (
          <Box 
            key={item.id} 
            sx={{ 
              py: 0.75, 
              px: 2, 
              borderBottom: '1px solid #eee', 
              display: 'flex', 
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              minHeight: 48
            }}
          >
            {/* Name / 4-Wheel Column */}
            <Box sx={{ 
              flex: '0 0 255px', 
              pr: 2,
              '@media (max-width: 1100px)': { flex: '0 0 220px' },
              '@media (max-width: 900px)': { flex: '1 1 100%', pr: 0, mb: 1 }
            }}>
              {isFourWheel ? (
                <FourWheelInput item={item} />
              ) : isEditingName ? (
                <TextField 
                  value={tempName} 
                  onChange={(e) => setTempName(e.target.value)} 
                  onBlur={handleSaveName} 
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()} 
                  size="small" 
                  fullWidth 
                  autoFocus 
                  placeholder="Enter item name..."
                />
              ) : (
                <Typography 
                  onClick={isCustom ? () => handleStartEdit(item) : undefined} 
                  sx={{ 
                    cursor: isCustom ? 'pointer' : 'default', 
                    fontWeight: 500, 
                    color: fontColor, 
                    pt: 0.5,
                    '&:hover': isCustom ? { textDecoration: 'underline' } : {}
                  }}
                >
                  {item.name || (isCustom ? 'Click to edit name' : 'Untitled Item')}
                </Typography>
              )}
            </Box>

            {/* Action Columns */}
            <Stack 
              direction="row" 
              spacing={1} 
              alignItems="center" 
              sx={{ 
                flex: 1, 
                justifyContent: 'flex-end', 
                gap: 1,
                flexWrap: 'nowrap',
                overflow: 'hidden',
                minWidth: 0
              }}
            >
              {actionHeaders.map((header) => {
                const { field } = header;
                const inputType = getInputType(field, item);
                if (inputType === 'remove') return null;

                const displayValue = getDisplayValue(item.id, field);
                const isChecked = isTruthy(displayValue);

                let colWidth = 46;
                if (inputType === 'text' || inputType === 'textarea') colWidth = 125;
                if (inputType === 'select') colWidth = 140;
                if (inputType === 'select-wide') colWidth = 'auto';
                if (inputType === 'radio') colWidth = 165;

                if (inputType === 'checkbox' || inputType === 'userstamp') {
                  return (
                    <Box key={field} sx={{ width: colWidth, textAlign: 'center', flexShrink: 0 }}>
                      <Checkbox 
                        checked={isChecked} 
                        onChange={() => handleToggle(item.id, field, displayValue)} 
                        size="small" 
                        sx={{ color: isChecked ? (header.true_color || '#4caf50') : '#aaa' }} 
                      />
                      {isChecked && inputType === 'userstamp' && (
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666', mt: 0.5 }}>
                          {item[`${field}_by_uid`] || 'You'}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                if (inputType === 'text' || inputType === 'textarea') {
                  return (
                    <Box key={field} sx={{ width: colWidth, flexShrink: 0 }}>
                      <TextField
                        value={displayValue}
                        onChange={(e) => handleTextInputChange(item.id, field, e.target.value)}
                        onBlur={() => handleTextCommit(item.id, field)}
                        onKeyDown={(e) => e.key === 'Enter' && handleTextCommit(item.id, field)}
                        size="small"
                        fullWidth
                        multiline={inputType === 'textarea'}
                        rows={inputType === 'textarea' ? 2 : 1}
                      />
                    </Box>
                  );
                }

                if (inputType === 'select') {
                  const preset = work_item_presets.find(p => p.id === item.work_item_preset_id) || {};
                  const optionsString = preset[`${field}_options`] || item[`${field}_options`] || '';
                  const options = optionsString.split(',').map(opt => opt.trim()).filter(Boolean);

                  return (
                    <Box key={field} sx={{ width: colWidth, flexShrink: 0 }}>
                      <Select
                        value={displayValue || ''}
                        onChange={(e) => handleTextCommit(item.id, field, e.target.value)}
                        size="small"
                        fullWidth
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Select...</MenuItem>
                        {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                      </Select>
                    </Box>
                  );
                }

                if (inputType === 'select-wide') {
                  const preset = work_item_presets.find(p => p.id === item.work_item_preset_id) || {};
                  const optionsString = preset[`${field}_options`] || item[`${field}_options`] || '';
                  const options = optionsString.split(',').map(opt => opt.trim()).filter(Boolean);

                  return (
                    <Box key={field} sx={{ flex: 1, minWidth: 160 }}>
                      <Select
                        value={displayValue || ''}
                        onChange={(e) => handleTextCommit(item.id, field, e.target.value)}
                        size="small"
                        fullWidth
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Select option...</MenuItem>
                        {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                      </Select>
                    </Box>
                  );
                }

                return <Box key={field} sx={{ width: 40, flexShrink: 0 }} />;
              })}
            </Stack>
          </Box>
        );
      })}

      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', pr: 2 }}>
        <Button variant="outlined" color="success" size="small" startIcon={<CheckCircleIcon />} onClick={() => onApproveAllSection?.(sectionId)}>
          Pass All in Section
        </Button>
        <Button variant="contained" color="primary" size="small" onClick={() => addCustomRow(sectionId)}>
          + Add Item
        </Button>
      </Box>
    </Box>
  );
};

export default DynamicSimpleChecklist;